/**
 * The Projects bucket: the work itself, in two segments.
 *
 * Projects answers "how is this one going" — hours against budget, tasks done,
 * and a one-tap way to put time on it. Tasks is the old Today and Upcoming
 * tabs folded together, because on a phone they were two taps apart and always
 * read in sequence anyway.
 *
 * Lifecycle and folder pickers are deliberately not here. They are filing
 * decisions, they belong on the project screen, and two dropdowns on every row
 * is what made the desktop table unusable at 375pt.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { card } from "@/components/atoms";
import { itemProjectId } from "@/components/ItemRow";
import { useChrome } from "@/context/ChromeContext";
import { useTaskEditor } from "@/context/TaskEditorContext";
import {
  subtaskCounts, useAllItems, useAllSubtasks, useProjects, useToggleItem, useUpdateItem,
} from "@/hooks/useData";
import { useTimeEntries } from "@/hooks/useTime";
import { flattenTree, itemsById as buildItemsById, projectTree } from "@/lib/deps";
import {
  isOverdue, isToday, isTomorrow, nextWeekRange, parseDate, plusWeek, scheduledFor, startOfDay, thisWeekRange,
} from "@/lib/format";
import { progressCounts } from "@/lib/hierarchy";
import { useShowCompleted } from "@/lib/theme";
import { hours } from "@/lib/timeRollup";
import type { Item, Priority, Project } from "@/lib/types";

import { SectionLabel, Segmented, screen } from "../components";
import { useLogSheet } from "../components/LogSheetContext";
import { MobileSection } from "./MobileList";
import { SearchSegment } from "./MobileSearch";

type Segment = "tasks" | "projects" | "search";

export function MobileProjects({ initialSegment = "tasks" }: { initialSegment?: Segment }) {
  const [segment, setSegment] = useState<Segment>(initialSegment);
  const { data: projects = [] } = useProjects();
  const { data: items = [] } = useAllItems();

  const live = projects.filter((p) => p.status !== "archived" && p.kind !== "inbox");
  const open = items.filter((i) => i.status !== "done");
  const late = open.filter(isOverdue).length;

  useChrome({
    title: "Tasks",
    subtitle: segment === "search"
      ? `${live.length} projects · ${open.length} open tasks`
      : `${live.length} active${late ? ` · ${late} overdue` : ""}`,
  }, [live.length, open.length, late, segment]);

  return (
    <div style={screen}>
      <Segmented
        value={segment}
        onChange={setSegment}
        options={[
          { value: "tasks", label: "Tasks" },
          { value: "projects", label: "Projects" },
          { value: "search", label: "Search" },
        ]}
      />
      {segment === "projects" && <ProjectsSegment />}
      {segment === "tasks" && <TasksSegment />}
      {segment === "search" && <SearchSegment />}
    </div>
  );
}

/* -- Projects --------------------------------------------------------------- */

function ProjectsSegment() {
  const logSheet = useLogSheet();
  const { data: projects = [] } = useProjects();
  const { data: items = [] } = useAllItems();
  const { data: entries = [] } = useTimeEntries();

  const live = projects.filter((p) => p.status !== "archived" && p.kind !== "inbox");

  const secondsByProject = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of entries) {
      if (!e.project) continue;
      map.set(e.project, (map.get(e.project) ?? 0) + (e.durationSeconds || 0));
    }
    return map;
  }, [entries]);

  const taskCounts = useMemo(() => {
    const map = new Map<string, { done: number; total: number }>();
    for (const i of items) {
      const id = itemProjectId(i);
      if (!id) continue;
      const c = map.get(id) ?? { done: 0, total: 0 };
      c.total += 1;
      if (i.status === "done") c.done += 1;
      map.set(id, c);
    }
    return map;
  }, [items]);

  // Sub-projects follow their parent, indented, rather than being stranded in a
  // flat alphabetical list.
  // At any depth: the same tree the sidebar and Projects page use.
  const ordered = useMemo(
    () => flattenTree(projectTree(live)).map(({ project, depth }) => ({ project, sub: depth > 0, depth })),
    [live],
  );


  if (ordered.length === 0) {
    return (
      <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--muted)" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>No projects yet</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>
          They come from Todoist — sync from Settings, or create one there.
        </div>
      </div>
    );
  }

  const renderGroup = (group: { project: Project; sub: boolean; depth: number }[]) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {group.map(({ project: p, sub, depth }) => (
        <ProjectCard
          key={p.id}
          project={p}
          sub={sub}
          depth={depth}
          seconds={secondsByProject.get(p.id) ?? 0}
          tasks={taskCounts.get(p.id) ?? { done: 0, total: 0 }}
          onLog={() => logSheet.open({ projectId: p.id })}
        />
      ))}
    </div>
  );

  return (
    <>
      {/* `live` already leaves archived projects out, so every one here is Active. */}
      <SectionLabel right={`${ordered.length}`}>Active</SectionLabel>
      {renderGroup(ordered)}
    </>
  );
}

function ProjectCard({
  project: p, sub, depth = 0, seconds, tasks, onLog,
}: {
  project: Project;
  sub: boolean;
  /** 0 top level, 1 a sub-project, 2 a sub-project's sub-project… */
  depth?: number;
  seconds: number;
  tasks: { done: number; total: number };
  onLog: () => void;
}) {
  const logged = seconds / 3600;
  const budget = p.budgetHours ?? 0;
  const percent = budget > 0 ? Math.min(100, Math.round((logged / budget) * 100)) : 0;
  const over = budget > 0 && logged > budget;

  const navigate = useNavigate();
  const open = () => navigate(`/project/${p.id}`);

  /**
   * The whole card opens the project, not just its title.
   *
   * Only the 35px title strip used to be a link, inside a 139px card — so a
   * tap aimed at the middle, which is where the hours and the bar are and
   * therefore where the eye goes, landed on nothing at all.
   */
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
      style={{ ...card, borderRadius: 14, padding: "13px 14px", marginLeft: sub ? 16 * Math.max(1, depth) : 0, cursor: "pointer" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }}>
        <span style={{ width: 10, height: 10, borderRadius: 3, background: p.color, flex: "none" }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: "block", fontSize: 15, fontWeight: 600, color: "var(--ink)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{p.name}</span>
          <span style={{
            display: "block", fontSize: 12, color: "var(--muted)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>{p.client || "Internal"}</span>
        </span>
        <span aria-hidden style={{ flex: "none", color: "var(--muted2)", fontSize: 15 }}>›</span>
      </div>

      {/* Hours first: on a phone this is the number you came to check. */}
      <div className="mono" style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        fontSize: 12, marginTop: 11, marginBottom: 5,
      }}>
        <span style={{ color: "var(--ink2)" }}>
          {hours(seconds)} h{budget > 0 && ` / ${budget}h`}
        </span>
        <span style={{ color: over ? "var(--bad)" : "var(--muted2)" }}>
          {budget > 0 ? `${percent}%` : "no budget"}
        </span>
      </div>
      <div style={{ height: 4, borderRadius: 999, background: "var(--tint2)", overflow: "hidden" }}>
        {budget > 0 && (
          <div style={{ height: "100%", width: `${percent}%`, background: over ? "var(--bad)" : p.color, borderRadius: 999 }} />
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", marginTop: 10 }}>
        <span style={{ fontSize: 12.5, color: "var(--muted)" }}>
          {tasks.total === 0 ? "No tasks" : `${tasks.done}/${tasks.total} tasks done`}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onLog(); }}
          style={{
            marginLeft: "auto", minHeight: 32, padding: "0 4px", border: "none", background: "none",
            color: "var(--accent)", fontSize: 13, fontWeight: 500, cursor: "pointer",
          }}
        >Log time</button>
      </div>
    </div>
  );
}

/* -- Tasks ------------------------------------------------------------------ */

const PRIORITY_WEIGHT: Record<Priority, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

/**
 * Overdue, today, tomorrow, the rest of this week, next week.
 *
 * Keyed off `scheduledFor` — the Todoist *date*, falling back to the deadline —
 * not `due` on its own. A task with a date and no deadline is the normal case
 * in Todoist, and keying off the deadline made every one of them invisible
 * here, which is exactly the bug that was fixed on the desktop lists.
 */
function TasksSegment() {
  const { data: items = [] } = useAllItems();
  const { data: projects = [] } = useProjects();
  const { data: subs = [] } = useAllSubtasks();
  const toggle = useToggleItem();
  const updateItem = useUpdateItem();
  const { openItem } = useTaskEditor();
  const [showCompleted, setShowCompleted] = useShowCompleted();

  const projectById = new Map<string, Project>(projects.map((p) => [p.id, p]));
  const byId = buildItemsById(items);
  const subCounts = progressCounts(items, subtaskCounts(subs));

  const open = items.filter((i) => showCompleted || i.status !== "done");
  const [, twEnd] = thisWeekRange();
  const [nwStart, nwEnd] = nextWeekRange();
  const today = startOfDay();

  // Within each day, filed by project (A–Z, the Inbox and unfiled last), most
  // urgent first within a project, then by date.
  const projectRank = new Map(projects.filter((p) => p.kind !== "inbox").map((p, n) => [p.id, n]));
  const byProject = (list: Item[]) => [...list].sort((a, b) => {
    const pa = projectRank.get(itemProjectId(a) || "") ?? Infinity;
    const pb = projectRank.get(itemProjectId(b) || "") ?? Infinity;
    if (pa !== pb) return pa - pb;
    if (a.priority !== b.priority) return PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
    return (scheduledFor(a) || "").localeCompare(scheduledFor(b) || "");
  });

  const on = (i: Item) => parseDate(scheduledFor(i));
  const overdue = byProject(open.filter(isOverdue));
  const dueToday = byProject(open.filter((i) => isToday(scheduledFor(i))));
  // Tomorrow wins over both weeks, so on a Sunday it is not filed under next week.
  const dueTomorrow = byProject(open.filter((i) => isTomorrow(scheduledFor(i))));
  const week = byProject(open.filter((i) => {
    const d = on(i);
    return d && !isOverdue(i) && !isToday(scheduledFor(i)) && !isTomorrow(scheduledFor(i)) && d > today && d < twEnd;
  }));
  const next = byProject(open.filter((i) => {
    const d = on(i);
    return d && !isTomorrow(scheduledFor(i)) && d >= nwStart && d < nwEnd;
  }));

  const defer = (i: Item) => updateItem.mutate({ id: i.id, due: plusWeek(i.due) });
  const common = { projectById, onToggle: toggle, onOpen: openItem, itemsById: byId, subCounts, allItems: items };

  const clear = overdue.length + dueToday.length + dueTomorrow.length + week.length + next.length === 0;
  if (clear) {
    return (
      <div style={{ padding: "48px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 30, marginBottom: 8 }}>✓</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Nothing scheduled</div>
        <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>
          Anything with a date in Todoist shows up here.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button
          onClick={() => setShowCompleted(!showCompleted)}
          style={{
            minHeight: 34, padding: "0 12px", borderRadius: 999, cursor: "pointer", fontSize: 12.5,
            border: `1px solid ${showCompleted ? "var(--accentLine)" : "var(--line2)"}`,
            background: showCompleted ? "var(--accentTint)" : "var(--surface)",
            color: showCompleted ? "var(--accent)" : "var(--muted)",
          }}
        >Completed {showCompleted ? "shown" : "hidden"}</button>
      </div>

      {overdue.length > 0 && (
        <MobileSection title="Overdue" items={overdue} {...common} onDefer={defer} />
      )}
      <MobileSection title="Today" items={dueToday} {...common} onDefer={defer}
        emptyText="Nothing scheduled for today." />
      {dueTomorrow.length > 0 && (
        <MobileSection title="Tomorrow" items={dueTomorrow} {...common} onDefer={defer} />
      )}
      {week.length > 0 && (
        <MobileSection title="Rest of this week" items={week} {...common} onDefer={defer} />
      )}
      {next.length > 0 && (
        <MobileSection title="Next week" items={next} {...common} onDefer={defer} />
      )}
    </div>
  );
}
